import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing';

/** What is being dragged (P16-4): boards (one or a selection, with where they are), a project card, a folder. */
export type DragPayload =
  | { kind: 'boards'; boards: readonly { id: string; projectId: string; folderId: string | null }[] }
  | { kind: 'project'; projectId: string }
  | { kind: 'folder'; projectId: string; folderId: string };

/**
 * Where it is dropped, read from `data-drop-*` attributes: a project card (`folderId` undefined: its root for boards,
 * a place in the order for projects), a folder section (`folderId`) or a project's root section (`folderId` null).
 */
export type DropTarget = { projectId: string; folderId: string | null | undefined; archived: boolean };

export function targetOf(el: Element | null): DropTarget | null {
  const node = el?.closest<HTMLElement>('[data-drop-project]');
  if (!node) return null;
  const { dropProject, dropFolder, dropArchived } = node.dataset;
  return { projectId: dropProject!, folderId: dropFolder === undefined ? undefined : dropFolder || null, archived: dropArchived === 'true' };
}

/** `none` covers every drop that would change nothing or is not allowed: no move, no toast, no Undo. */
export function dropEffect(drag: DragPayload, target: DropTarget): 'move' | 'reorder' | 'none' {
  if (drag.kind === 'boards') {
    if (target.archived) return 'none';
    const folderId = target.folderId ?? null;
    return drag.boards.every((b) => b.projectId === target.projectId && b.folderId === folderId) ? 'none' : 'move';
  }
  if (drag.kind === 'project') return target.folderId === undefined && target.projectId !== drag.projectId ? 'reorder' : 'none';
  return typeof target.folderId === 'string' && target.projectId === drag.projectId && target.folderId !== drag.folderId ? 'reorder' : 'none';
}

/**
 * New keys after moving `movedId` onto `targetId` in `list` (in its current order): forward lands after the target,
 * backward before it. One key when the other keys are strictly ordered, else the whole list is renumbered (ties:
 * every project starts at 'a0'). Only changed keys are returned.
 */
export function reorder(list: readonly { id: string; position: string }[], movedId: string, targetId: string): Map<string, string> {
  const from = list.findIndex((x) => x.id === movedId);
  const at = list.findIndex((x) => x.id === targetId);
  if (from < 0 || at < 0 || from === at) return new Map();
  const rest = list.filter((x) => x.id !== movedId);
  const index = rest.findIndex((x) => x.id === targetId) + (from < at ? 1 : 0);
  const prev = rest[index - 1]?.position ?? null;
  const next = rest[index]?.position ?? null;
  // Ties anywhere would leave the order to the last update: then the whole list gets keys of its own.
  const strict = rest.every((x, i) => i === 0 || rest[i - 1]!.position < x.position);
  try {
    if (strict) return new Map([[movedId, generateKeyBetween(prev, next)]]);
  } catch {
    // An invalid key (written by hand): renumber below.
  }
  const ordered = [...rest.slice(0, index), list[from]!, ...rest.slice(index)];
  const keys = generateNKeysBetween(null, null, ordered.length);
  return new Map(ordered.map((x, i) => [x.id, keys[i]!] as const).filter(([id, key]) => list.find((x) => x.id === id)!.position !== key));
}
